/**
 * Tooltip Utility Functions
 *
 * This module provides utilities for generating structured tooltip HTML with two-column layout.
 *
 * @example
 * // Basic usage
 * const fieldConfig = [
 *   { key: "id", label: "ID" },
 *   { key: "name", label: "Name" },
 *   { key: "area", label: "Area", format: value => formatNumber(value, "km²") },
 *   { key: "url", label: "Link", format: formatUrl },
 * ];
 *
 * const tooltipHTML = generateTooltipHTML("Layer Title", data, fieldConfig, {
 *   width: "500px",
 *   height: "400px",
 *   wrapValues: true,
 *   naValue: "N/A"
 * });
 *
 * @example
 * // In a layer file
 * const createMyLayerTooltip = data => {
 *   return generateTooltipHTML("My Layer", data, myFieldConfig, {
 *     width: "450px",
 *     height: "300px",
 *     wrapValues: true,
 *   });
 * };
 *
 * // Field configuration options:
 * // - key: Data property name
 * // - label: Display label for the field
 * // - format: Optional formatting function (formatNumber, formatUrl, custom)
 *
 * // Tooltip configuration options:
 * // - width: Fixed tooltip width (default: "450px")
 * // - height: Fixed tooltip height (default: "350px")
 * // - labelWidth: Width of label column (default: "40%")
 * // - valueWidth: Width of value column (default: "60%")
 * // - wrapValues: Whether to wrap long values (default: true)
 * // - naValue: Text to show for null/empty values (default: "N/A")
 */

import {NumberUtil} from "base/utilities";

/**
 * Convert data object to tooltip fields array
 * @param {Object} data - The data object
 * @param {Array} fieldConfig - Array of field configurations
 * @returns {Array} Array of tooltip field objects
 */
export const createTooltipFields = (data, fieldConfig) => {
    return fieldConfig.map(config => {
        const value = data[config.key];
        return {
            label: config.label,
            value: value,
            format: config.format || null,
        };
    });
};

/**
 * Check if a value is null, undefined, empty, or only whitespace
 * @param {any} value - The value to check
 * @returns {boolean} True if the value is considered empty
 */
export const isEmptyValue = value => {
    if (value == null || value === undefined) return true;
    if (typeof value === "string") {
        return value.trim() === "";
    }
    return false;
};

/**
 * Generate tooltip HTML with two-column layout
 *
 * @param {string} title - Tooltip title
 * @param {Object} data - The data object containing the values
 * @param {Array} fieldConfig - Array of field configurations
 * @param {Object} tooltipConfig - Tooltip configuration options
 * @returns {string} HTML string for tooltip
 *
 * @example
 * const data = { id: 1, name: "Example", area: 123.45 };
 * const fieldConfig = [
 *   { key: "id", label: "ID" },
 *   { key: "name", label: "Name" },
 *   { key: "area", label: "Area", format: value => formatNumber(value, "km²") }
 * ];
 *
 * const html = generateTooltipHTML("My Layer", data, fieldConfig, {
 *   width: "500px",
 *   height: "400px",
 *   wrapValues: true,
 *   naValue: "—"
 * });
 */
export const generateTooltipHTML = (title, data, fieldConfig, tooltipConfig = {}) => {
    const {
        width = "450px",
        height = "350px",
        labelWidth = "40%",
        valueWidth = "60%",
        wrapValues = true,
        naValue = "N/A",
    } = tooltipConfig;

    const fields = createTooltipFields(data, fieldConfig);

    let html = `
        <div class="custom-tooltip" style="
            width: ${width};
            height: ${height};
        ">
            <div class="tooltip-title" style="
                font-weight: bold;
                font-size: 12px;
                margin-bottom: 8px;
                color: #333;
                border-bottom: 1px solid #ccc;
                padding-bottom: 3px;
            ">${title}</div>
            <div class="tooltip-content" style="display: flex; flex-direction: column; gap: 4px;">
    `;

    fields.forEach(field => {
        const labelStyle = `
            font-weight: bold;
            color: #555;
            white-space: nowrap;
            min-width: ${labelWidth};
            display: inline-block;
        `;

        const valueStyle = `
            color: #333;
            ${wrapValues ? "word-wrap: break-word; white-space: normal;" : "white-space: nowrap;"}
            min-width: ${valueWidth};
            display: inline-block;
        `;

        let displayValue = field.value;

        // Apply formatting if specified
        if (field.format && typeof field.format === "function") {
            displayValue = field.format(field.value);
        }

        // Handle null/empty values centrally
        if (isEmptyValue(displayValue)) {
            displayValue = naValue;
        }

        html += `
            <div class="tooltip-row" style="display: flex; gap: 8px; align-items: flex-start;">
                <div class="tooltip-label" style="${labelStyle}">${field.label}:</div>
                <div class="tooltip-value" style="${valueStyle}">${displayValue}</div>
            </div>
        `;
    });

    html += `
            </div>
        </div>
    `;

    return html;
};

/**
 * Helper function to format numbers with NumberUtil
 *
 * @param {number} value - The number to format
 * @param {string} unit - Optional unit to append
 * @returns {string|null} Formatted number string or null if invalid
 *
 * @example
 * formatNumber(123.456, "km²") // Returns "123.46 km²"
 * formatNumber(null, "km²")    // Returns null
 * formatNumber("", "km²")      // Returns null
 */
export const formatNumber = (value, unit = "") => {
    if (isEmptyValue(value)) return null;

    const formatted = NumberUtil.formatNumber(value);
    return unit ? `${formatted} ${unit}` : formatted;
};

/**
 * Helper function to format URLs as clickable links
 *
 * @param {string} value - The URL to format
 * @returns {string|null} HTML link string or null if invalid
 *
 * @example
 * formatUrl("https://example.com") // Returns "<a href="https://example.com" target="_blank">https://example.com</a>"
 * formatUrl(null)                  // Returns null
 * formatUrl("")                    // Returns null
 */
export const formatUrl = value => {
    if (isEmptyValue(value)) return null;
    return `<a href="${value}" target="_blank" style="color: #0066cc; text-decoration: underline;">${value}</a>`;
};
